/**
 * CT Shop — filter tabs, custom sort dropdown, wishlist
 */
(() => {
  const STORAGE_KEY = 'ct-wishlist-handles';

  /** @returns {Set<string>} */
  const readWishlist = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(list) ? list.map(String) : []);
    } catch {
      return new Set();
    }
  };

  /** @param {Set<string>} set */
  const writeWishlist = (set) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
    } catch {
      /* private mode */
    }
  };

  /**
   * @param {HTMLElement} root
   * @param {Set<string>} set
   */
  const syncWish = (root, set) => {
    root.querySelectorAll('[data-ct-shop-wishlist]').forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      const handle = btn.dataset.productHandle || '';
      const on = handle !== '' && set.has(handle);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('is-active', on);
    });
  };

  /**
   * @param {HTMLElement} root
   * @param {string} filter
   */
  const applyFilter = (root, filter) => {
    const grid = root.querySelector('[data-ct-shop-grid]');
    const bundles = root.querySelector('[data-ct-shop-bundles]');
    const empty = root.querySelector('[data-ct-shop-empty]');
    const cards = [...root.querySelectorAll('[data-ct-shop-card]')];

    const showBundlesOnly = filter === 'bundles';
    if (grid instanceof HTMLElement) {
      grid.hidden = showBundlesOnly;
      grid.setAttribute('aria-hidden', showBundlesOnly ? 'true' : 'false');
    }
    if (bundles instanceof HTMLElement) {
      bundles.hidden = false;
      if (showBundlesOnly) {
        bundles.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    let visible = 0;
    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const keys = (card.dataset.filter || 'all').split(/\s+/);
      const match = filter === 'all' || filter === 'bundles' ? filter === 'all' : keys.includes(filter);
      const show = filter === 'bundles' ? false : match;
      card.hidden = !show;
      card.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) visible += 1;
    });

    if (empty instanceof HTMLElement) {
      empty.hidden = showBundlesOnly || visible > 0;
    }

    root.dataset.activeFilter = filter;
  };

  /**
   * @param {HTMLElement} root
   * @param {string} sort
   */
  const applySort = (root, sort) => {
    const grid = root.querySelector('[data-ct-shop-grid]');
    if (!(grid instanceof HTMLElement)) return;
    const cards = [...grid.querySelectorAll('[data-ct-shop-card]')];
    const keyed = cards.map((card, index) => ({
      card,
      index,
      title: (card.getAttribute('data-title') || '').toLowerCase(),
      price: Number.parseInt(card.getAttribute('data-price') || '0', 10) || 0,
    }));

    keyed.sort((a, b) => {
      switch (sort) {
        case 'price-asc':
          return a.price - b.price || a.index - b.index;
        case 'price-desc':
          return b.price - a.price || a.index - b.index;
        case 'title-asc':
          return a.title.localeCompare(b.title) || a.index - b.index;
        case 'title-desc':
          return b.title.localeCompare(a.title) || a.index - b.index;
        default:
          return a.index - b.index;
      }
    });

    keyed.forEach(({ card }) => grid.appendChild(card));
    root.dataset.activeSort = sort;
  };

  /**
   * @param {HTMLElement} sortRoot
   * @returns {HTMLButtonElement[]}
   */
  const getSortOptions = (sortRoot) =>
    [...sortRoot.querySelectorAll('[data-ct-shop-sort-option]')].filter(
      (el) => el instanceof HTMLButtonElement
    );

  /**
   * @param {HTMLElement} sortRoot
   * @param {boolean} open
   * @param {{ focusOption?: boolean }} [opts]
   */
  const setSortOpen = (sortRoot, open, opts = {}) => {
    const trigger = sortRoot.querySelector('[data-ct-shop-sort-trigger]');
    const menu = sortRoot.querySelector('[data-ct-shop-sort-menu]');
    if (!(trigger instanceof HTMLButtonElement) || !(menu instanceof HTMLElement)) return;
    sortRoot.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    menu.hidden = !open;
    if (open && opts.focusOption !== false) {
      const active =
        getSortOptions(sortRoot).find((btn) => btn.classList.contains('is-active')) ||
        getSortOptions(sortRoot)[0];
      active?.focus();
    }
  };

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} sortRoot
   * @param {string} value
   * @param {string} label
   */
  const setSortValue = (root, sortRoot, value, label) => {
    const valueEl = sortRoot.querySelector('[data-ct-shop-sort-value]');
    const trigger = sortRoot.querySelector('[data-ct-shop-sort-trigger]');
    if (valueEl) valueEl.textContent = label;
    getSortOptions(sortRoot).forEach((btn) => {
      const on = btn.dataset.value === value;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    applySort(root, value);
    setSortOpen(sortRoot, false, { focusOption: false });
    if (trigger instanceof HTMLButtonElement) trigger.focus();
  };

  /** @param {HTMLElement} root */
  const bindSort = (root) => {
    const sortRoot = root.querySelector('[data-ct-shop-sort]');
    if (!(sortRoot instanceof HTMLElement)) {
      applySort(root, 'featured');
      return;
    }

    const trigger = sortRoot.querySelector('[data-ct-shop-sort-trigger]');
    const menu = sortRoot.querySelector('[data-ct-shop-sort-menu]');
    if (!(trigger instanceof HTMLButtonElement) || !(menu instanceof HTMLElement)) return;

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const willOpen = !sortRoot.classList.contains('is-open');
      setSortOpen(sortRoot, willOpen, { focusOption: willOpen });
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setSortOpen(sortRoot, true);
      }
    });

    menu.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const option = target.closest('[data-ct-shop-sort-option]');
      if (!(option instanceof HTMLButtonElement) || !menu.contains(option)) return;
      event.preventDefault();
      const value = option.dataset.value || 'featured';
      const label = option.textContent?.trim() || value;
      setSortValue(root, sortRoot, value, label);
    });

    menu.addEventListener('keydown', (event) => {
      const options = getSortOptions(sortRoot);
      if (!options.length) return;
      const current = document.activeElement;
      const index = options.findIndex((btn) => btn === current);

      if (event.key === 'Escape') {
        event.preventDefault();
        setSortOpen(sortRoot, false, { focusOption: false });
        trigger.focus();
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        options[0].focus();
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        options[options.length - 1].focus();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const next = options[(index + 1 + options.length) % options.length] || options[0];
        next.focus();
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prev = options[(index - 1 + options.length) % options.length] || options[0];
        prev.focus();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        if (!(current instanceof HTMLButtonElement) || !options.includes(current)) return;
        event.preventDefault();
        const value = current.dataset.value || 'featured';
        const label = current.textContent?.trim() || value;
        setSortValue(root, sortRoot, value, label);
      }
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Node) || sortRoot.contains(target)) return;
      setSortOpen(sortRoot, false, { focusOption: false });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!sortRoot.classList.contains('is-open')) return;
      setSortOpen(sortRoot, false, { focusOption: false });
      trigger.focus();
    });

    applySort(root, root.dataset.activeSort || 'featured');
  };

  /** @param {HTMLElement} root */
  const bind = (root) => {
    if (root.dataset.ctShopReady === 'true') return;
    root.dataset.ctShopReady = 'true';

    const wish = readWishlist();
    syncWish(root, wish);

    root.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const wishBtn = target.closest('[data-ct-shop-wishlist]');
      if (wishBtn instanceof HTMLButtonElement && root.contains(wishBtn)) {
        event.preventDefault();
        event.stopPropagation();
        const handle = wishBtn.dataset.productHandle || '';
        if (!handle) return;
        if (wish.has(handle)) wish.delete(handle);
        else wish.add(handle);
        writeWishlist(wish);
        syncWish(root, wish);
        window.dispatchEvent(
          new CustomEvent('ct:wishlist-change', {
            detail: { handle, active: wish.has(handle), handles: [...wish] },
          })
        );
        return;
      }

      const tab = target.closest('[data-ct-shop-tab]');
      if (tab instanceof HTMLButtonElement && root.contains(tab)) {
        event.preventDefault();
        const filter = tab.dataset.ctShopTab || 'all';
        root.querySelectorAll('[data-ct-shop-tab]').forEach((el) => {
          const on = el === tab;
          el.classList.toggle('is-active', on);
          el.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        applyFilter(root, filter);
      }
    });

    bindSort(root);
    applyFilter(root, root.dataset.activeFilter || 'all');
  };

  const init = () => {
    document.querySelectorAll('[data-ct-shop]').forEach((el) => {
      if (el instanceof HTMLElement) bind(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
